import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { familiesApi, Family } from '../api/families';
import { sharingApi, SharedMemberItem } from '../api/sharing';
import { documentsApi, RecentDocumentItem } from '../api/documents';
import { useIsAdvanced } from '../utils/viewMode';
import { formatRelativeTimeRo } from '../utils/dates';
import DashboardHero from '../components/dashboard/DashboardHero';
import DashboardStatsRow, {
  DashStat,
} from '../components/dashboard/DashboardStatsRow';
import InFlightPipelineStrip from '../components/dashboard/InFlightPipelineStrip';
import RecentActivityFeed from '../components/dashboard/RecentActivityFeed';
import FamiliesPanel from '../components/dashboard/FamiliesPanel';
import SharedMembersPanel from '../components/dashboard/SharedMembersPanel';
import QuickInsightsPanel, {
  QuickInsight,
} from '../components/dashboard/QuickInsightsPanel';
import AdminShortcutCard from '../components/dashboard/AdminShortcutCard';
import './Dashboard.css';

/** Live-refresh tick interval while at least one document is in
 *  flight. Cheap polling beats wiring an SSE multiplexer for the
 *  dashboard alone — the recent endpoint is a single small query. */
const IN_FLIGHT_POLL_MS = 8_000;

type SidePanelTab = 'families' | 'shared';

/** Time-of-day Romanian greeting prefix. Pure UI nicety; no i18n
 *  framework needed for three buckets. */
function greetingForHour(hour: number): string {
  if (hour < 5) return 'Bună seara';
  if (hour < 12) return 'Bună dimineața';
  if (hour < 18) return 'Bună ziua';
  return 'Bună seara';
}

const IconDocs = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const IconUsers = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconFamily = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconSpinner = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
    <path d="M21 12a9 9 0 1 1-6.22-8.56" />
  </svg>
);

const IconAlert = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconClock = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

/** Stage tokens that count as "in flight" for the pipeline strip. */
const IN_FLIGHT_STAGES = new Set([
  'queued',
  'processing_started',
  'loading_images',
  'ocr_extraction',
  'processing',
  'summary_generating',
  'classification_generating',
]);

/** Pipeline stages that indicate a permanent (visible-to-user) error. */
const ERROR_STAGES = new Set(['error', 'failed', 'summary_error', 'classification_error']);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const isAdvanced = useIsAdvanced();
  const [families, setFamilies] = useState<Family[]>([]);
  const [shared, setShared] = useState<SharedMemberItem[]>([]);
  const [recent, setRecent] = useState<RecentDocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sideTab, setSideTab] = useState<SidePanelTab>('families');

  const loadFamiliesAndShared = useCallback(async () => {
    try {
      const [fams, sharedRows] = await Promise.all([
        familiesApi.getFamilies(),
        sharingApi.listInboundMembers().catch((): SharedMemberItem[] => []),
      ]);
      setFamilies(fams);
      setShared(sharedRows);
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setError(detail || 'Eroare la încărcarea datelor');
    }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const rows = await documentsApi.getRecentDocuments(5);
      setRecent(rows);
    } catch (err: unknown) {
      // Recent feed is non-essential; surface a per-section hint
      // rather than blocking the whole page.
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      // Don't clobber a real top-level error if one was already set.
      setError((prev) => prev || detail || 'Eroare la încărcarea activității recente');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      await Promise.all([loadFamiliesAndShared(), loadRecent()]);
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadFamiliesAndShared, loadRecent]);

  // ── Derived aggregates ────────────────────────────────────────

  const allMemberIds = useMemo(() => {
    const own = families.flatMap((f) => f.members.map((m) => m.id));
    const inbound = shared.map((s) => s.member_id);
    return new Set([...own, ...inbound]);
  }, [families, shared]);

  const totalMembers = allMemberIds.size;
  const totalFamilies = families.length;

  const totalDocuments = useMemo(() => {
    let n = 0;
    for (const f of families) {
      for (const m of f.members) {
        n += m.stats?.document_count ?? 0;
      }
    }
    for (const s of shared) {
      n += s.stats?.document_count ?? 0;
    }
    return n;
  }, [families, shared]);

  const inFlightCount = useMemo(() => {
    let n = 0;
    for (const f of families) {
      for (const m of f.members) {
        n += m.stats?.pending_documents ?? 0;
      }
    }
    for (const s of shared) {
      n += s.stats?.pending_documents ?? 0;
    }
    return n;
  }, [families, shared]);

  const erroredCount = useMemo(() => {
    return recent.filter(
      (d) => d.pipeline_status != null && ERROR_STAGES.has(d.pipeline_status)
    ).length;
  }, [recent]);

  const lastUploadIso = useMemo(() => {
    if (recent.length === 0) return null;
    // Server returns sorted desc by upload_date — first row is freshest.
    return recent[0].upload_date;
  }, [recent]);

  const inFlightItems = useMemo(() => {
    return recent.filter(
      (d) => d.pipeline_status != null && IN_FLIGHT_STAGES.has(d.pipeline_status)
    );
  }, [recent]);

  const docCountByFamily = useMemo(() => {
    const out: Record<number, number> = {};
    for (const f of families) {
      out[f.id] = f.members.reduce(
        (acc, m) => acc + (m.stats?.document_count ?? 0),
        0
      );
    }
    return out;
  }, [families]);

  const pendingByFamily = useMemo(() => {
    const out: Record<number, number> = {};
    for (const f of families) {
      out[f.id] = f.members.reduce(
        (acc, m) => acc + (m.stats?.pending_documents ?? 0),
        0
      );
    }
    return out;
  }, [families]);

  const sharedFamilyIds = useMemo(() => {
    const out = new Set<number>();
    if (!user) return out;
    for (const f of families) {
      if (f.owner_id !== user.id) out.add(f.id);
    }
    return out;
  }, [families, user]);

  // ── Quick insights (advanced view only) ────────────────────────

  const insights = useMemo((): QuickInsight[] => {
    const out: QuickInsight[] = [];
    if (erroredCount > 0) {
      out.push({
        id: 'errors',
        tone: 'danger',
        title:
          erroredCount === 1
            ? '1 document cu eroare în pipeline'
            : `${erroredCount} documente cu erori în pipeline`,
        body: 'Verifică ultimele documente din feed-ul de activitate. Poți regenera rezumatul sau reclasifica direct din pagina documentului.',
      });
    }
    if (inFlightCount > 0 && erroredCount === 0) {
      out.push({
        id: 'inflight',
        tone: 'info',
        title:
          inFlightCount === 1
            ? '1 document în procesare acum'
            : `${inFlightCount} documente în procesare acum`,
        body: 'Pipeline-ul (OCR → rezumat → clasificare) rulează în fundal. Strip-ul de mai sus se actualizează la câteva secunde.',
      });
    }
    if (totalDocuments > 0 && lastUploadIso) {
      const days = Math.floor(
        (Date.now() - new Date(lastUploadIso).getTime()) / (24 * 60 * 60 * 1000)
      );
      if (days >= 14) {
        out.push({
          id: 'stale',
          tone: 'warning',
          title: `Niciun document încărcat în ultimele ${days} zile`,
          body: 'Adaugă rezultatele analizelor sau scrisori medicale recente — vor fi clasificate și incluse automat în cronologii.',
          to: '/families',
          ctaLabel: 'Mergi la familii',
        });
      }
    }
    return out.slice(0, 3);
  }, [erroredCount, inFlightCount, totalDocuments, lastUploadIso]);

  // ── Live refresh while pipeline is busy ────────────────────────

  useEffect(() => {
    // Only poll when there's something in-flight to refresh.
    if (inFlightItems.length === 0) return;
    const t = window.setInterval(() => {
      // Refresh both the feed (drives the in-flight strip) and the
      // family/shared stats so the stat tiles + side panels reflect
      // pipeline completion without a manual reload.
      loadRecent();
      loadFamiliesAndShared();
    }, IN_FLIGHT_POLL_MS);
    return () => window.clearInterval(t);
  }, [inFlightItems.length, loadRecent, loadFamiliesAndShared]);

  // ── Display helpers ────────────────────────────────────────────

  const greetingName =
    user?.full_name?.trim() || user?.email?.split('@')[0] || 'Utilizator';
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString('ro-RO', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    []
  );
  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);

  // ── Stat tiles ────────────────────────────────────────────────

  const stats = useMemo<DashStat[]>(() => {
    const lastUploadLabel = lastUploadIso
      ? formatRelativeTimeRo(lastUploadIso) || '—'
      : '—';
    const lastUploadHint = lastUploadIso ? 'Ultima încărcare' : 'Niciun upload încă';
    if (!isAdvanced) {
      // Simplified view: 3 stat tiles, no technical telemetry.
      return [
        {
          id: 'docs',
          tone: 'teal',
          icon: <IconDocs />,
          label: 'Documente',
          value: totalDocuments,
          hint:
            totalDocuments === 1
              ? 'în toate dosarele tale'
              : 'în toate dosarele tale',
        },
        {
          id: 'members',
          tone: 'blue',
          icon: <IconUsers />,
          label: 'Membri',
          value: totalMembers,
          hint: totalMembers === 1 ? 'persoană urmărită' : 'persoane urmărite',
        },
        {
          id: 'last',
          tone: 'slate',
          icon: <IconClock />,
          label: 'Ultima activitate',
          value: <span className="dash-stat-value-small">{lastUploadLabel}</span>,
          hint: lastUploadHint,
        },
      ];
    }
    // Advanced view: full 5-tile breakdown.
    const tiles: DashStat[] = [
      {
        id: 'docs',
        tone: 'teal',
        icon: <IconDocs />,
        label: 'Documente totale',
        value: totalDocuments,
        hint: 'în toate dosarele accesibile',
      },
      {
        id: 'members',
        tone: 'blue',
        icon: <IconUsers />,
        label: 'Membri activi',
        value: totalMembers,
        hint:
          shared.length > 0
            ? `${families.flatMap((f) => f.members).length} propriii · ${shared.length} partajați`
            : 'persoane în dosar',
      },
      {
        id: 'families',
        tone: 'violet',
        icon: <IconFamily />,
        label: 'Familii',
        value: totalFamilies,
        hint: totalFamilies === 1 ? 'grup de îngrijire' : 'grupuri de îngrijire',
      },
    ];
    tiles.push({
      id: 'inflight',
      tone: inFlightCount > 0 ? 'amber' : 'slate',
      icon: <IconSpinner />,
      label: 'În procesare',
      value: inFlightCount,
      pulse: inFlightCount > 0,
      hint: inFlightCount > 0 ? 'pipeline OCR / AI activ' : 'nimic în coadă',
    });
    if (erroredCount > 0) {
      tiles.push({
        id: 'errors',
        tone: 'red',
        icon: <IconAlert />,
        label: 'Cu erori',
        value: erroredCount,
        hint: 'documente recente cu probleme',
      });
    } else {
      tiles.push({
        id: 'last',
        tone: 'slate',
        icon: <IconClock />,
        label: 'Ultima încărcare',
        value: <span className="dash-stat-value-small">{lastUploadLabel}</span>,
        hint: lastUploadHint,
      });
    }
    return tiles;
  }, [
    isAdvanced,
    totalDocuments,
    totalMembers,
    totalFamilies,
    inFlightCount,
    erroredCount,
    lastUploadIso,
    families,
    shared.length,
  ]);

  // ── Loading skeleton ──────────────────────────────────────────

  if (loading) {
    return (
      <div className="page-container dashboard-page">
        <div className="dash-skel-hero" aria-hidden />
        <div className="dash-skel-stats">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="dash-skel-stat" />
          ))}
        </div>
        <div className="dash-skel-bento">
          <div className="dash-skel-panel dash-skel-panel--tall" />
          <div className="dash-skel-panel" />
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────

  const showAdminCard = Boolean(user?.is_admin);
  const showInsights = isAdvanced && insights.length > 0;
  const hasAnyFamily = families.length > 0;
  const hasSharedMembers = shared.length > 0;

  return (
    <div className="page-container dashboard-page">
      <DashboardHero
        greetingName={greetingName}
        todayLabel={todayLabel}
        greeting={greeting}
        isAdvanced={isAdvanced}
        hasAnyFamily={hasAnyFamily}
        hasSharedMembers={hasSharedMembers}
      />

      {error && (
        <div className="admin-flash admin-flash--error" role="alert">
          <span className="admin-flash__icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </span>
          <span className="admin-flash__text">{error}</span>
        </div>
      )}

      <DashboardStatsRow stats={stats} />

      {isAdvanced && <InFlightPipelineStrip items={inFlightItems} />}

      {/* Families / Shared members — Mobile: tabbed switcher.
          Desktop: rendered side-by-side via CSS grid. */}
      {hasSharedMembers ? (
        <>
          <div className="tabs-bar tabs-bar--pills dash-side-tabs" role="tablist" aria-label="Familii și membri partajați">
            <button
              type="button"
              role="tab"
              aria-selected={sideTab === 'families'}
              className={`tabs-bar__tab dash-side-tab${sideTab === 'families' ? ' is-active' : ''}`}
              onClick={() => setSideTab('families')}
            >
              Familiile mele
              <span className="tabs-bar__count dash-side-tab-count">{families.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sideTab === 'shared'}
              className={`tabs-bar__tab dash-side-tab${sideTab === 'shared' ? ' is-active' : ''}`}
              onClick={() => setSideTab('shared')}
            >
              Partajate cu mine
              <span className="tabs-bar__count dash-side-tab-count">{shared.length}</span>
            </button>
          </div>
          <div className="dash-side-grid">
            <div
              className={`dash-side-col${sideTab === 'families' ? ' is-active' : ''}`}
            >
              <FamiliesPanel
                families={families}
                docCountByFamily={docCountByFamily}
                pendingByFamily={pendingByFamily}
                sharedFamilyIds={sharedFamilyIds}
              />
            </div>
            <div
              className={`dash-side-col${sideTab === 'shared' ? ' is-active' : ''}`}
            >
              <SharedMembersPanel members={shared} />
            </div>
          </div>
        </>
      ) : (
        <div className="dash-side-grid dash-side-grid--single">
          <FamiliesPanel
            families={families}
            docCountByFamily={docCountByFamily}
            pendingByFamily={pendingByFamily}
            sharedFamilyIds={sharedFamilyIds}
          />
        </div>
      )}

      {/* Recent activity — full-width row below the Families/Shared block. */}
      <RecentActivityFeed items={recent} isAdvanced={isAdvanced} />

      {/* Quick insights — full-width when present, sits between Recent and Admin. */}
      {showInsights && <QuickInsightsPanel insights={insights} />}

      {/* Admin shortcut — its own full-width row at the very bottom. */}
      {showAdminCard && (
        <div className="dash-admin-row">
          <AdminShortcutCard />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
